import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload, X, Image as ImageIcon, Ruler } from "lucide-react";
import { PROPERTY_TYPES, LOCALITIES as FALLBACK_LOCALITIES, AMENITIES_BY_CATEGORY, SQFT_PER_BIGHA, isLandType } from "@/data/constants";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FeePreviewDialog from "@/components/site/FeePreviewDialog";

const MAX_IMAGES = 5;

async function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const MAX = 1400;
        let w = img.width, h = img.height;
        if (w > MAX) { h = h * (MAX / w); w = MAX; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PostProperty({ onLoginClick }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [f, setF] = useState({
    purpose: "sale", category: "residential", property_type: "",
    title: "", description: "", price: "",
    locality: "", address: "",
    bedrooms: "", bathrooms: "", parking: "", area: "", built_up_area: "", carpet_area: "",
    floor: "", total_floors: "", age_bracket: "", furnished: "",
    amenities: [], images: [],
    owner_name: user?.name || "", owner_phone: user?.phone || "", owner_whatsapp: user?.phone || "", owner_email: user?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feeOpen, setFeeOpen] = useState(false);
  const [localities, setLocalities] = useState(FALLBACK_LOCALITIES);
  useEffect(() => {
    api.get("/localities").then((r) => {
      if (Array.isArray(r.data) && r.data.length) setLocalities(r.data.map((l) => l.name));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleAmenity = (a) => set("amenities", f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a]);

  const landMode = useMemo(() => isLandType(f.category, f.property_type), [f.category, f.property_type]);
  const bigha = useMemo(() => {
    const a = parseFloat(f.area);
    if (!a || !landMode) return null;
    return (a / SQFT_PER_BIGHA);
  }, [f.area, landMode]);
  const amenities = useMemo(() => {
    if (landMode) return AMENITIES_BY_CATEGORY[f.category === "land" ? "land" : "plot"];
    return AMENITIES_BY_CATEGORY[f.category] || AMENITIES_BY_CATEGORY.residential;
  }, [f.category, landMode]);
  // If the amenity list changes (e.g. residential -> land), drop selections no longer valid
  useEffect(() => {
    setF((s) => ({ ...s, amenities: s.amenities.filter((a) => amenities.includes(a)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenities.join("|")]);

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - f.images.length;
    if (remaining <= 0) { toast.error(`Maximum ${MAX_IMAGES} photos allowed`); e.target.value = ""; return; }
    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Only ${remaining} more photo(s) can be added`);
    setUploading(true);
    try {
      const results = [];
      for (const file of toProcess) {
        if (!file.type.startsWith("image/")) { toast.error(`${file.name} is not an image`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
        results.push(await resizeImage(file));
      }
      set("images", [...f.images, ...results]);
    } catch { toast.error("Failed to read image"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (i) => set("images", f.images.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return onLoginClick?.();
    if (!f.price || parseFloat(f.price) <= 0) return toast.error(t("post.errPrice"));
    if (f.images.length === 0) return toast.error(t("post.errImage"));
    if (!f.title || !f.property_type || !f.locality || !f.area || !f.owner_name || !f.owner_phone) return toast.error(t("post.errRequired"));
    setFeeOpen(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        price: parseFloat(f.price),
        area: parseFloat(f.area),
        // Land-mode: force these out so admin CRUD stays clean
        bedrooms: landMode ? null : (f.bedrooms ? parseInt(f.bedrooms) : null),
        bathrooms: landMode ? null : (f.bathrooms ? parseInt(f.bathrooms) : null),
        parking: landMode ? null : (f.parking ? parseInt(f.parking) : null),
        built_up_area: landMode ? null : (f.built_up_area ? parseFloat(f.built_up_area) : null),
        carpet_area: landMode ? null : (f.carpet_area ? parseFloat(f.carpet_area) : null),
        floor: landMode ? null : (f.floor ? parseInt(f.floor) : null),
        total_floors: landMode ? null : (f.total_floors ? parseInt(f.total_floors) : null),
        age_years: null,
        age_bracket: landMode ? null : (f.age_bracket || null),
        bigha: landMode && f.area ? Number((parseFloat(f.area) / SQFT_PER_BIGHA).toFixed(3)) : null,
        price_label: f.purpose === "sale" ? `₹${Number(f.price).toLocaleString("en-IN")}` : `₹${Number(f.price).toLocaleString("en-IN")} / Month`,
      };
      await api.post("/properties", payload);
      toast.success(t("post.success"));
      setFeeOpen(false);
      nav("/dashboard/properties");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); } finally { setSubmitting(false); }
  };

  const types = PROPERTY_TYPES[f.category] || [];
  const req = <span className="text-kp-maroon">*</span>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="post-property-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900">{t("post.title")}</h1>
        <p className="text-slate-500 mt-1">{t("post.sub")}</p>
      </div>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div><label className="text-xs font-semibold text-slate-600">{t("post.purpose")} {req}</label>
            <Select value={f.purpose} onValueChange={(v) => set("purpose", v)}><SelectTrigger data-testid="pp-purpose"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="sale">{t("post.sale")}</SelectItem><SelectItem value="rent">{t("post.rent")}</SelectItem><SelectItem value="lease">{t("post.lease")}</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">{t("post.category")} {req}</label>
            <Select value={f.category} onValueChange={(v) => { set("category", v); set("property_type",""); }}>
              <SelectTrigger data-testid="pp-category"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">{t("post.residential")}</SelectItem>
                <SelectItem value="commercial">{t("post.commercial")}</SelectItem>
                <SelectItem value="industrial">{t("post.industrial")}</SelectItem>
                <SelectItem value="warehouse">{t("post.warehouse")}</SelectItem>
                <SelectItem value="plot" data-testid="pp-cat-plot">Plot</SelectItem>
                <SelectItem value="land" data-testid="pp-cat-land">Land</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">{t("post.ptype")} {req}</label>
            <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger data-testid="pp-type"><SelectValue placeholder={t("post.chooseType")}/></SelectTrigger><SelectContent>{types.map(tp => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>

        <div><label className="text-xs font-semibold text-slate-600">{t("post.ptitle")} {req}</label>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder={t("post.ptitle_ph")} data-testid="pp-title"/>
        </div>
        <div><label className="text-xs font-semibold text-slate-600">{t("post.description")}</label>
          <Textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder={t("post.desc_ph")} data-testid="pp-description"/>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600">
              {f.purpose === "rent" ? t("post.rentMonthly") : f.purpose === "lease" ? t("post.leaseMonthly") : t("post.price")} {req}
            </label>
            <Input required type="number" min="1" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder={f.purpose !== "sale" ? "₹ / month" : "₹"} data-testid="pp-price"/>
          </div>
          {!landMode && (
            <div><label className="text-xs font-semibold text-slate-600">{t("post.furnishing")}</label>
              <Select value={f.furnished} onValueChange={(v) => set("furnished", v)}><SelectTrigger><SelectValue placeholder={t("post.choose")}/></SelectTrigger><SelectContent><SelectItem value="Furnished">{t("post.furnished")}</SelectItem><SelectItem value="Semi-Furnished">{t("post.semi")}</SelectItem><SelectItem value="Unfurnished">{t("post.unfurnished")}</SelectItem></SelectContent></Select>
            </div>
          )}
        </div>

        {!landMode && (f.category === "residential" || f.category === "commercial" || f.category === "industrial") && (
          <div>
            <label className="text-xs font-semibold text-slate-600">{t("post.ageOfConstruction")}</label>
            <Select value={f.age_bracket || ""} onValueChange={(v) => set("age_bracket", v)}>
              <SelectTrigger data-testid="pp-age-bracket"><SelectValue placeholder={t("post.choose")}/></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">{t("post.ageNew")}</SelectItem>
                <SelectItem value="1-5">{t("post.age15")}</SelectItem>
                <SelectItem value="6-10">{t("post.age610")}</SelectItem>
                <SelectItem value="10+">{t("post.age10plus")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="text-xs font-semibold text-slate-600">{t("post.locality")} {req}</label>
            <Select value={f.locality} onValueChange={(v) => set("locality", v)}><SelectTrigger data-testid="pp-locality"><SelectValue placeholder={t("post.choose")}/></SelectTrigger><SelectContent className="max-h-72">{localities.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">{t("post.address")}</label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder={t("post.addr_ph")}/></div>
        </div>

        {landMode ? (
          <div className="rounded-xl border border-kp-teal/20 bg-kp-teal-light/40 p-4 space-y-3" data-testid="pp-land-metrics">
            <div className="text-xs font-bold uppercase tracking-widest text-kp-teal flex items-center gap-1.5"><Ruler size={13}/> Land Measurement</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Area (Sq. Ft.) {req}</label>
                <Input type="number" min="0" value={f.area} onChange={(e) => set("area", e.target.value)} placeholder="e.g. 17427" data-testid="pp-area"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Area in Bigha (auto)</label>
                <div className="h-10 rounded-md border border-slate-200 bg-white px-3 flex items-center text-sm font-semibold text-slate-900 tabular-nums" data-testid="pp-bigha">
                  {bigha != null ? `${bigha.toFixed(3)} Bigha` : <span className="text-slate-400 font-normal">Enter area to see Bigha</span>}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">1 Bigha (Surat / Gujarati Bigha) = {SQFT_PER_BIGHA.toLocaleString("en-IN")} sq ft · Bigha is auto-computed and saved with the listing.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-4 gap-4">
            <div><label className="text-xs font-semibold text-slate-600">{t("post.area")} {req}</label><Input type="number" value={f.area} onChange={(e) => set("area", e.target.value)} data-testid="pp-area"/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.bedrooms")}</label><Input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.bathrooms")}</label><Input type="number" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.parking")}</label><Input type="number" value={f.parking} onChange={(e) => set("parking", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.builtup")}</label><Input type="number" value={f.built_up_area} onChange={(e) => set("built_up_area", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.carpet")}</label><Input type="number" value={f.carpet_area} onChange={(e) => set("carpet_area", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.floor")}</label><Input type="number" value={f.floor} onChange={(e) => set("floor", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">{t("post.totalFloors")}</label><Input type="number" value={f.total_floors} onChange={(e) => set("total_floors", e.target.value)}/></div>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-slate-600">{t("post.amenities")} · <span className="text-slate-400 capitalize">{landMode ? "Land" : f.category}</span></label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="pp-amenities">
            {amenities.map((a) => <button type="button" key={a} onClick={() => toggleAmenity(a)} className={`text-xs px-3 py-1.5 rounded-full border ${f.amenities.includes(a) ? "bg-kp-teal text-white border-kp-teal" : "bg-white text-slate-700 border-slate-300 hover:border-kp-teal"}`}>{a}</button>)}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600">{t("post.images")} {req}</label>
          <p className="text-xs text-slate-500 mt-1">{t("post.imagesHint")}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            {f.images.map((u, i) => (
              <div key={i} className="relative w-28 h-24 rounded-lg overflow-hidden border border-slate-200 group">
                <img src={u} className="w-full h-full object-cover" alt=""/>
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`pp-remove-image-${i}`}><X size={12}/></button>
                <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 rounded">{i + 1}</span>
              </div>
            ))}
            {f.images.length < MAX_IMAGES && (
              <label className={`w-28 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 cursor-pointer hover:border-kp-teal hover:text-kp-teal transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="pp-upload-images">
                {uploading ? <ImageIcon size={22} className="animate-pulse"/> : <><Upload size={20}/><span className="text-[11px] mt-1">{t("post.uploadPhotos")}</span><span className="text-[10px] text-slate-400">{f.images.length}/{MAX_IMAGES}</span></>}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onFileChange} data-testid="pp-file-input"/>
              </label>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
          <div><label className="text-xs font-semibold text-slate-600">{t("post.ownerName")} {req}</label><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="pp-owner-name"/></div>
          <div><label className="text-xs font-semibold text-slate-600">{t("post.mobile")} {req}</label><Input value={f.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} data-testid="pp-owner-phone"/></div>
          <div><label className="text-xs font-semibold text-slate-600">{t("post.whatsapp")}</label><Input value={f.owner_whatsapp} onChange={(e) => set("owner_whatsapp", e.target.value)}/></div>
          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-600">{t("post.ownerEmail")}</label>
            <Input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="you@example.com" data-testid="pp-owner-email"/>
            <p className="text-xs text-slate-500 mt-1">{t("post.ownerEmailHint")}</p>
          </div>
        </div>

        <div className="bg-kp-teal-light border border-kp-teal/20 text-kp-teal text-sm rounded-xl p-3">{t("post.note")}</div>

        <Button type="submit" disabled={submitting || uploading} className="btn-primary w-full rounded-full h-12 font-semibold" data-testid="pp-submit">
          {submitting ? t("post.submitting") : t("post.submit")}
        </Button>
      </form>
      <FeePreviewDialog open={feeOpen} onOpenChange={setFeeOpen} purpose={f.purpose} price={f.price} locality={f.locality} onConfirm={confirmSubmit} submitting={submitting}/>
    </div>
  );
}
